<template>
  <v-card outlined class="mt-2">
    <v-toolbar flat dense :color="$vuetify.theme.dark ? 'blue-grey darken-3' : 'blue-grey lighten-5'"
      :dark="$vuetify.theme.dark">
      <v-toolbar-title class="subtitle-2">Wholesale Conditions</v-toolbar-title>
      <v-spacer />
      <v-chip v-if="activeTier" small color="green" text-color="white">Tier applies</v-chip>
    </v-toolbar>

    <div class="px-3 pt-2">
      <v-alert v-if="activeTier" dense outlined type="success" class="mb-2">
        <div>
          Applies at quantity ≥ <strong>{{ asQty(activeTier.minQuantity) }}</strong>.
          Unit price → <strong>{{ money(activeTier.effectiveUnitPrice) }}</strong>
          <span v-if="perUnitLess(activeTier) > 0">
            (you save {{ money(perUnitLess(activeTier)) }} / unit)
          </span>
        </div>
        <div v-if="nextTier" class="mt-1">
          Next tier at <strong>{{ asQty(nextTier.minQuantity) }}</strong>:
          unit price {{ money(nextTier.effectiveUnitPrice) }}
          <small class="grey--text">&nbsp;(+ reach in {{ remainingToNext }} more)</small>
        </div>
      </v-alert>

      <v-alert v-else dense outlined type="info" class="mb-2">
        No tier applies yet. First threshold: <strong>{{ firstThreshold }}</strong>.
      </v-alert>
    </div>

    <v-data-table class="elevation-0" :headers="headers" :items="rows" disable-pagination hide-default-footer dense>
      <template v-slot:item.minQuantity="{ item }">
        <span :class="rowClass(item)">{{ asQty(item.minQuantity) }}</span>
      </template>

      <template v-slot:item.discountPercent="{ item }">
        <span :class="rowClass(item)">{{ item.discountPercent }}</span>
      </template>

      <template v-slot:item.discountFixed="{ item }">
        <span :class="rowClass(item)">{{ money(item.discountFixed) }}</span>
      </template>

      <template v-slot:item.effectiveUnitPrice="{ item }">
        <strong :class="rowClass(item)">{{ money(item.effectiveUnitPrice) }}</strong>
      </template>

      <template v-slot:item.applies="{ item }">
        <v-icon v-if="applies(item)" color="green" small>mdi-check-circle</v-icon>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
export default {
  name: 'WholesaleConditions',
  props: {
    conditions: { type: Array, default: () => [] },
    quantity: { type: Number, default: 0 },
    retail: { type: Number, default: 0 },
  },
  computed: {
    headers() {
      return [
        { text: 'Min Qty (≥)', value: 'minQuantity' },
        { text: 'Discount %', value: 'discountPercent' },
        { text: 'Less ₱/unit', value: 'discountFixed' },
        { text: 'Effective ₱/unit', value: 'effectiveUnitPrice' },
        { text: '', value: 'applies', align: 'center', sortable: false },
      ]
    },
    rows() {
      const sorted = [...(this.conditions || [])]
        .filter(c => c && (c.is_active === undefined || c.is_active === 1))
        .sort((a, b) => Number(a.minQuantity) - Number(b.minQuantity))

      return sorted.map(c => {
        const percent = Number(c.discountPercentage) || 0
        const fixed = Number(c.discountFixed) || 0
        const percentLess = this.retail * (percent / 100)
        const perUnitLess = Math.max(percentLess, fixed)
        const effective = Math.max(this.retail - perUnitLess, 0)

        return {
          ...c,
          _percent: percent,
          _fixed: fixed,
          _perUnitLess: perUnitLess,
          discountPercent: `${percent.toFixed(2)}%`,
          discountFixed: Number(fixed.toFixed(2)),
          effectiveUnitPrice: Number(effective.toFixed(2)),
        }
      })
    },
    activeTier() {
      const applicable = this.rows.filter(r => this.quantity >= Number(r.minQuantity))
      if (!applicable.length) return null
      return applicable[applicable.length - 1]
    },
    nextTier() {
      if (!this.rows.length) return null
      return this.rows.find(r => this.quantity < Number(r.minQuantity)) || null
    },
    firstThreshold() {
      if (!this.rows.length) return '—'
      return `≥ ${this.asQty(this.rows[0].minQuantity)}`
    },
    remainingToNext() {
      if (!this.nextTier) return 0
      const need = Number(this.nextTier.minQuantity) - (Number(this.quantity) || 0)
      return need > 0 ? need : 0
    },
  },
  watch: {
    quantity: {
      immediate: true,
      handler() { this.emitApply() },
    },
    retail() {
      this.emitApply()
    },
    conditions: {
      deep: true,
      handler() { this.emitApply() },
    },
  },
  methods: {
    applies(item) {
      return this.quantity >= Number(item.minQuantity)
    },
    rowClass(item) {
      return this.applies(item) ? 'green--text text--darken-2 font-weight-medium' : ''
    },
    perUnitLess(row) {
      return Number(row._perUnitLess || 0)
    },
    asQty(q) {
      const n = Number(q || 0)
      return Number.isInteger(n) ? n : n.toFixed(2)
    },
    money(n) {
      const val = Number(n || 0)
      return `₱${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    emitApply() {
      if (!this.activeTier) {
        this.$emit('apply', { hasTier: false })
        return
      }
      const perUnitLess = this.perUnitLess(this.activeTier)
      const totalLess = Number((perUnitLess * (this.quantity || 0)).toFixed(2))
      const percent = Number(this.activeTier._percent || 0)

      this.$emit('apply', {
        hasTier: true,
        totalDiscount: totalLess,
        perUnitLess,
        percentRate: percent,
        effectiveUnitPrice: this.activeTier.effectiveUnitPrice,
        minQty: Number(this.activeTier.minQuantity),
      })
    },
  },
}
</script>